import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { RoleEnum } from 'app/modules/shared/enums/roleEnum';
import { User } from 'app/core/user/user.types';
import { AuthUtils } from '../auth.utils';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';
import { of, switchMap } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { UserUtil } from 'app/modules/shared/utils/user.util';

/**
 * Auth guard for the affiliate self-service portal.
 *
 * Sibling of {@code TeamAuthGuard} but matches strictly on {@code ROLE_AFFILIATE}
 * (the dedicated AffiliateUser chain). Anonymous users are redirected to the
 * affiliate sign-in page.
 */
export const AffiliateAuthGuard: CanActivateFn | CanActivateChildFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const userService = inject(UserService);

    return authService.check().pipe(
        switchMap((authenticated: boolean) => {
            if (!authenticated) {
                authService.signOut();
                const urlTree = router.parseUrl(
                    PathEnum.APP_AFFILIATE + '/' + PathEnum.APP_AFFILIATE_SIGN_IN +
                    `?redirectURL=${buildUrlPath(state)}`
                );
                return of(urlTree);
            }

            return userService.user$.pipe(
                switchMap(user => {
                    if (!user) {
                        console.error("Erreur d'initialisation de l'application");
                        return of(router.createUrlTree([PathEnum.ERROR, PathEnum.ERROR_500]));
                    }
                    const allowedRoles = (route.data['allowedRoles'] as RoleEnum[]) ?? [RoleEnum.AFF];
                    if (!checkAffiliateRoles(user, allowedRoles)) {
                        console.error("User don't have the affiliate role for this page");
                        return of(router.createUrlTree([PathEnum.ERROR, PathEnum.ERROR_403]));
                    }
                    return of(true);
                })
            );
        })
    );
};

function buildUrlPath(state: UrlSegment[] | RouterStateSnapshot) {
    let path = '';
    if (Array.isArray(state)) {
        let i = 0;
        while (i < state.length) {
            path += state[i].path + '/';
            i++;
        }
        path = path.substring(0, path.length - 1);
    } else {
        path = state.url;
    }
    return path;
}

function checkAffiliateRoles(user: User, allowedRoles: RoleEnum[]): boolean {
    if (!allowedRoles || allowedRoles.length === 0 || allowedRoles.includes(RoleEnum.ALL)) {
        return true;
    }
    // The portal is reserved to the dedicated AffiliateUser chain.
    if (!UserUtil.isAffiliateUser(user)) {
        return false;
    }
    return user.roles.some(role => allowedRoles.includes(role as RoleEnum));
}
